'use client'

import { useMemo, useEffect, useState } from 'react'
// import '@/styles/match-card.css' // Old CSS - commented out
import '@/styles/new-match-card.css'

// OLD FILTER IMPORTS - COMMENTED OUT
/*
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faTv, 
  faCircle, 
  faFutbol, 
  faBasketballBall, 
  faGamepad,
  faCalendarDay 
} from '@fortawesome/free-solid-svg-icons'
*/

import LiveMatchCard from './LiveMatchCard'
import GlassMatchCard from './GlassMatchCard'
import NewMatchCard from './NewMatchCard'
import SportsFilter from './SportsFilter'
import { useHomepage, prefetchHomepageData } from '@/hooks/useHomepage'
import { Match } from '@/lib/database'

// Filter counts interface
interface FilterCounts {
  liveToday: any[];
  liveTomorrow: any[];
  isLive: any[];
  liveTops: any[];
  liveCaster: any[];
  liveAll: any[];
}

// OLD FILTER CONFIG - COMMENTED OUT
/*
const filters = [
  { icon: faTv, text: 'Tất cả', type: 'liveAll' as const, isActive: true },
  { icon: faCircle, text: 'Đang live', type: 'isLive' as const },
  { icon: faFutbol, text: 'Bóng đá', type: 'liveFootball' as const },
  { icon: faBasketballBall, text: 'Bóng rổ', type: 'liveBasketball' as const },
  { icon: faGamepad, text: 'Esport', type: 'liveEsport' as const },
  { icon: faCalendarDay, text: 'Hôm nay', type: 'liveToday' as const },
]

type FilterType = 'liveAll' | 'isLive' | 'liveFootball' | 'liveBasketball' | 'liveEsport' | 'liveToday';
*/

export default function MatchList() {
  // State for filtered matches from SportsFilter
  const [filteredMatches, setFilteredMatches] = useState<Match[]>([]);
  const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null);
  const [isUsingFilter, setIsUsingFilter] = useState(false);

  // OLD FILTER LOGIC - COMMENTED OUT (Now using SportsFilter component)
  /*
  const [activeFilter, setActiveFilter] = useState<FilterType>('liveAll')
  */
  
  // 🚀 USE CACHE SYSTEM - Homepage data với Redis cache (fallback when no filter active)
  const { 
    liveMatches, 
    upcomingMatches, 
    loading: homepageLoading, 
    error: homepageError, 
    refetch,
    fromCache 
  } = useHomepage()

  // Handle filter changes from SportsFilter
  const handleFilterChange = (matches: Match[], counts: FilterCounts) => {
    setFilteredMatches(matches);
    setFilterCounts(counts);
    setIsUsingFilter(true);
  };

  // Show filtered matches if filter is active, otherwise show all matches
  const matches = useMemo(() => {
    if (isUsingFilter && filteredMatches.length > 0) {
      return filteredMatches;
    }
    return [...liveMatches, ...upcomingMatches];
  }, [isUsingFilter, filteredMatches, liveMatches, upcomingMatches]);

  // Loading state - show loading if homepage is loading OR if we're using filter and no matches yet
  const loading = homepageLoading || (isUsingFilter && filteredMatches.length === 0);

  // Error state
  const error = homepageError;

  // Show error cards if there's an error
  if (error && matches.length === 0) {
    return (
      <div>
        {/* NEW Sports Filter Component */}
        <SportsFilter onFilterChange={handleFilterChange} />
        
        {/* Error Cards */}
        <div className="fs-matches-wrapper">
          <div className="fs-matches-grid">
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className="fs-match-card" style={{
                background: '#2e2e2e',
                borderRadius: '7px',
                padding: '20px',
                textAlign: 'center',
                color: '#fff',
                border: '1px solid #ff4444'
              }}>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontSize: '24px' }}>❌</span>
                </div>
                <div style={{ fontSize: '14px', color: '#ff4444', marginBottom: '8px' }}>
                  Lỗi tải dữ liệu
                </div>
                <div style={{ fontSize: '12px', color: '#999', lineHeight: 1.4 }}>
                  {error}
                </div>
                <button 
                  onClick={() => window.location.reload()}
                  style={{
                    marginTop: '10px',
                    padding: '6px 12px',
                    background: '#ff4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Thử lại
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // OLD FILTER LOGIC - COMMENTED OUT
  /*
  // Combine và filter data based on active filter
  const matches = useMemo(() => {
    const allMatches = [...liveMatches, ...upcomingMatches]
    
    switch (activeFilter) {
      case 'liveAll':
        return allMatches
      case 'isLive':
        return allMatches.filter((match: Match) => match.match_status === '3')
      case 'liveFootball':
        return allMatches.filter((match: Match) => 
          match.match_type === 'football' || match.match_type === 'soccer' || !match.match_type
        )
      case 'liveBasketball':
        return allMatches.filter((match: Match) => match.match_type === 'basketball')
      case 'liveEsport':
        return allMatches.filter((match: Match) => match.match_type === 'esport')
      case 'liveToday':
        const today = new Date().toDateString()
        return allMatches.filter((match: Match) => {
          if (!match.time_start) return false
          return new Date(match.time_start).toDateString() === today
        })
      default:
        return allMatches
    }
  }, [liveMatches, upcomingMatches, activeFilter])

  const handleFilterChange = (filterType: FilterType) => {
    setActiveFilter(filterType)
  }
  */

  // Prefetch data on component mount to warm up cache
  useEffect(() => {
    prefetchHomepageData()
  }, [])

  // Auto refresh every 2 minutes to complement ISR (less aggressive)
  useEffect(() => {
    const interval = setInterval(() => {
      refetch()
    }, 120000) // 2 minutes - works well with 5min ISR

    return () => clearInterval(interval)
  }, [refetch])

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">Lỗi tải dữ liệu: {error}</p>
        <button 
          onClick={refetch}
          className="bg-[var(--button-primary)] text-white px-4 py-2 rounded hover:bg-[color-mix(in_srgb,var(--button-primary),#000_10%)]"
        >
          Thử lại
        </button>
      </div>
    )
  }

  return (
    <div className="mb-8" data-testid="match-list">
      {/* OLD Filter Tabs - COMMENTED OUT */}
      {/* 
      <div
        className="flex items-center gap-2 overflow-x-auto p-3 md:p-4 mb-4 rounded-xl border border-white/10 hide-scrollbar
                   bg-white/5 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
        style={{ backgroundColor: 'var(--glass-bg)', borderColor: 'var(--glass-border)' }}
      >
        {filters.map((filter) => (
          <button
            key={filter.type}
            onClick={() => handleFilterChange(filter.type)}
            className={`flex items-center gap-1 md:gap-2 px-2 py-1 md:px-4 md:py-2 rounded-2xl md:rounded-3xl transition-all whitespace-nowrap border uppercase font-bold text-[10px] md:text-sm
              backdrop-blur-md ring-1 ring-white/5 hover:ring-white/10
              ${
                activeFilter === filter.type
                  ? 'text-white shadow-[0_6px_20px_rgba(0,0,0,0.25)]'
                  : 'text-white hover:shadow-[0_6px_20px_rgba(0,0,0,0.25)]'
              }`}
            style={
              activeFilter === filter.type
                ? { backgroundColor: 'color-mix(in srgb, var(--filter-active-bg) 90%, transparent)', borderColor: 'var(--filter-active-border)' }
                : { backgroundColor: 'var(--filter-inactive-bg)', borderColor: 'var(--filter-inactive-border)' }
            }
          >
            <FontAwesomeIcon 
              icon={filter.icon} 
              className={`text-xs md:text-sm ${
                filter.type === 'isLive' ? 'text-red-500' : ''
              }`} 
            />
            {filter.text}
          </button>
        ))}
      </div>
      */}

      {/* NEW Sports Filter Component */}
      <SportsFilter onFilterChange={handleFilterChange} />

      {/* Matches Grid */}
      <div className="fs-matches-wrapper">
        <div className="fs-matches-grid">
          {loading && matches.length === 0 ? (
            // Loading skeleton
            [...Array(6)].map((_, i) => (
              <div key={i} className="fs-match-card loading" 
                data-match-id={`loading-${i}`} 
                data-api-id="loading"
                data-match-type="loading">
                
                {/* Clickable overlay - disabled for skeleton */}
                <div 
                  className="redirectPopup" 
                  style={{
                    position: 'absolute', 
                    inset: '0', 
                    pointerEvents: 'none',
                    cursor: 'default',
                    zIndex: 1
                  }}
                ></div>

                {/* Match Header */}
                <div className="fs-match-header">
                  <div className="gmd-tournament-header">
                    <div className="gmd-match-league">
                      <div className="gmd-comp_logo" style={{backgroundColor: '#666', borderRadius: '2px'}}></div>
                      <span className="text-ellipsis" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '3px', height: '11px'}}>Loading...</span>
                    </div>
                  </div>
                  <div className="gmd-elapsed-time">
                    <div className="gmd-elapsed-wrapper icon-haflt" data-fid="4wyrn4h694e4q86">
                      <div className="gmd-elapsed" style={{backgroundColor: '#666', width: '100%', height: '100%', borderRadius: '5px'}}></div>
                      <p className="gmd-e_minutes grid-match__status--normal t_time time t_time_num" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', fontSize: '12px', fontWeight: '700', padding: '2px 8px', width: '60px', height: '12px'}}>Loading</p>
                    </div>
                  </div>
                  <div className="fs-match-time">
                    <span style={{backgroundColor: '#666', color: 'transparent', borderRadius: '13px', padding: '5px 15px', fontSize: '12px', display: 'inline-block'}}>00:00 00/00</span>
                  </div>
                </div>

                {/* Match Content */}
                <div className="fs-match-content">
                  {/* Home Team */}
                  <div className="fs-team home">
                    <div style={{width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#666'}}></div>
                    <span className="fs-team-name" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', width: '80px', height: '12px', display: 'inline-block'}}>Loading</span>
                  </div>

                  {/* Match Center */}
                  <div className="fs-match-center">
                    <div className="fs-match-score">
                      <div className="grid-match__status gmd-mid_score text-center" data-fid="dj2ryoh1op7zq1z">
                        <div className="fs-score __vs" style={{backgroundColor: '#666', color: 'transparent'}}>VS</div>
                      </div>
                    </div>
                  </div>

                  {/* Away Team */}
                  <div className="fs-team away">
                    <div style={{width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#666'}}></div>
                    <span className="fs-team-name" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', width: '80px', height: '12px', display: 'inline-block'}}>Loading</span>
                  </div>
                </div>

                {/* Match Footer */}
                <div className="fs-match-footer">
                  <div className="gmd-match-footer__flag">
                    <div className="grid-match--flag">
                      <div className="gmd-match__goal-scoring d-flex">
                        <span className="gmd-ht" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', fontSize: '11px', width: '18px', height: '11px', display: 'inline-block'}}>HT</span>
                        <span className="gmd-match__half-court halfCourt" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', fontSize: '11px', width: '30px', height: '11px', display: 'inline-block', marginLeft: '3px'}}>0-0</span>
                      </div>
                      <div className="gmd-match__corner-ball d-flex">
                        <div style={{backgroundColor: '#666', width: '12px', height: '12px', borderRadius: '2px'}}></div>
                        <span className="gmd-match__corner-goal cornerGoal" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', fontSize: '11px', width: '30px', height: '11px', display: 'inline-block', marginLeft: '3px'}}>0-0</span>
                      </div>
                      <div className="gmd-match__corner-yellow d-flex">
                        <div style={{backgroundColor: '#666', width: '10px', height: '10px', borderRadius: '2px'}}></div>
                        <span className="gmd-match__home-yellow cornerYellow" style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', fontSize: '11px', width: '30px', height: '11px', display: 'inline-block', marginLeft: '3px'}}>0-0</span>
                      </div>
                    </div>
                  </div>
                  <div className="gmd-match-footer__streamer number-blv-1 max-three-blv">
                    <div className="extra-info_one extra-info_list int-blv">
                      <div style={{backgroundColor: '#666', borderRadius: '50%', width: '35px', height: '35px'}}></div>
                      <span style={{backgroundColor: '#666', color: 'transparent', borderRadius: '2px', fontSize: '12px', fontWeight: '500', width: '40px', height: '16px', display: 'inline-block'}}>BLV</span>
                    </div>
                  </div>
                  <div className="gmd-match-footer__odds">
                    <a 
                      href="#" 
                      className="odds-comp" 
                      target="_blank" 
                      rel="nofollow"
                      style={{position: 'relative', zIndex: 2, pointerEvents: 'none'}}
                    >
                      <div style={{backgroundColor: '#666', borderRadius: '3px', width: '70px', height: '32px'}}></div>
                    </a>
                  </div>
                </div>
              </div>
            ))
          ) : matches.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-400">
              Không có trận đấu nào
            </div>
          ) : (
            matches.map((match, index) => (
              <NewMatchCard 
                key={`${match.id}-${match.caster || 'default'}-${index}`} 
                match={match}
                isLoaded={true}
                priority={index < 4} // First 4 cards load immediately (above-the-fold)
              />
            ))
          )}
        </div>
      </div>

      {/* Load-more removed: show all */}
    </div>
  )
} 